import { Avatar, Box, Button, Divider, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { MdAssignment, MdSchedule, MdVerified } from "react-icons/md";
import bg from "../../../assets/icons/exams.svg";
import { examRedoRequest } from "../../../modules/students-modules/exams/Actions";
import { studentPalette } from "../../../constants/studentPalette";

const POST_EXAM_REDIRECT_KEY = "edutopia_post_exam_redirect";

const C = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  border: studentPalette.stone,
  cream: studentPalette.cream,
  page: studentPalette.warmBeige,
  muted: "rgba(91, 13, 22, 0.65)",
};

const FONT = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;

const ExamMainData = ({
  exam,
  state,
  solveExamFun,
  checkExamAnswersFun,
  returnTo,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const isSolved = state === "Solved";
  const timed = exam?.timer == 1;

  const persistReturnAndStart = () => {
    if (returnTo) {
      try {
        sessionStorage.setItem(POST_EXAM_REDIRECT_KEY, returnTo);
      } catch {
        /* ignore */
      }
    }
    solveExamFun(exam);
  };

  return (
    <VStack
      align="stretch"
      spacing={4}
      w="100%"
      fontFamily={FONT}
      bg="white"
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 8px 28px rgba(91, 13, 22, 0.08)"
    >
      <Box position="relative" pl={4} py={5} pr={5} bg={`linear-gradient(135deg, ${C.page} 0%, white 55%)`}>
        <Box position="absolute" left={0} top={0} bottom={0} w="4px" bg={C.primary} borderRadius="full" />
        <HStack align="flex-start" spacing={4}>
          <Avatar name={exam?.name} src={bg} borderRadius="lg" size="md" bg="transparent" borderWidth="1px" borderColor={C.border} />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontWeight="800" color={C.accent} fontSize="md" noOfLines={2} lineHeight="short">
              {exam?.name}
            </Text>
            <Text fontSize="xs" color={C.muted} fontWeight="600">
              {exam?.created_at ? dayjs(exam.created_at).format("D MMM YYYY") : ""}
            </Text>
          </VStack>
        </HStack>
      </Box>

      <VStack align="stretch" spacing={3} px={5} pb={2}>
        <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
          <HStack color={C.primary}>
            <MdVerified size={18} />
            <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.14em">
              Status
            </Text>
          </HStack>
          <Box
            px={3}
            py={1}
            borderRadius="full"
            bg={isSolved ? "rgba(91, 13, 22, 0.12)" : "rgba(192, 74, 88, 0.18)"}
            color={C.accent}
            fontSize="xs"
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            {state}
          </Box>
        </Flex>

        {timed ? (
          <Flex align="center" justify="space-between" fontSize="sm" color={C.accent}>
            <HStack>
              <MdSchedule size={18} />
              <Text fontWeight="700">Time limit</Text>
            </HStack>
            <Text fontWeight="800">{exam?.time} min</Text>
          </Flex>
        ) : null}

        {exam?.total_count != null ? (
          <Flex align="center" justify="space-between" fontSize="sm" color={C.muted}>
            <HStack>
              <MdAssignment size={18} />
              <Text fontWeight="700">Questions</Text>
            </HStack>
            <Text fontWeight="800" color={C.primary}>
              {exam.total_count}
            </Text>
          </Flex>
        ) : null}

        {isSolved && exam?.score != null ? (
          <>
            <Divider borderColor={C.border} />
            <Box borderRadius="xl" bg={C.page} px={4} py={3} borderWidth="1px" borderColor={C.border}>
              <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.12em" mb={1}>
                Your grade
              </Text>
              <Text fontSize="2xl" fontWeight="800" color={C.primary} lineHeight="1.2">
                {exam?.right_answers == null ? "—" : exam.right_answers}
                <Text as="span" fontSize="md" fontWeight="700" color={C.muted}>
                  {" "}
                  / {exam.score}
                </Text>
              </Text>
            </Box>
          </>
        ) : null}
      </VStack>

      <VStack px={5} pb={5} pt={0} spacing={3} align="stretch">
        {isSolved ? (
          <HStack w="100%" spacing={3}>
            <Button
              flex={1}
              bg={C.primary}
              color="white"
              borderRadius="xl"
              fontWeight="800"
              size="md"
              onClick={() => checkExamAnswersFun(exam)}
              isDisabled={exam?.correction == 1 ? false : true}
              _hover={{ bg: C.accent }}
            >
              Check answers
            </Button>
            <Button
              flex={1}
              isLoading={isLoading}
              variant="outline"
              borderWidth="2px"
              borderColor={C.primary}
              color={C.primary}
              borderRadius="xl"
              fontWeight="800"
              onClick={() => {
                setLoading(true);
                const formData = new FormData();
                formData.append("exam_id", exam?.id);
                const action = () => setLoading(false);
                dispatch(examRedoRequest(formData, action));
              }}
              isDisabled={exam?.redo_flag == 1 ? false : true}
            >
              Request redo
            </Button>
          </HStack>
        ) : (
          <Button
            w="100%"
            bg={C.primary}
            color="white"
            borderRadius="xl"
            fontWeight="800"
            size="lg"
            py={7}
            isDisabled={exam?.enable == 0 ? false : true}
            onClick={persistReturnAndStart}
            _hover={{ bg: C.accent }}
            boxShadow="0 10px 24px rgba(91, 13, 22, 0.2)"
          >
            Start your exam
          </Button>
        )}
      </VStack>
    </VStack>
  );
};

export default ExamMainData;
